// ABOUTME: Resolves the localized names of content type groups, content types and select options from the options.
// ABOUTME: Falls back to the default locale, then to any name, then to the code when no name is known.

import { EligibleContentType, FindReplaceOptions, LocalizedName } from '../types';

export type ContentTypeLabeler = {
    groupLabel: (ctg: string) => string;
    typeLabel: (ctg: string, ct: string) => string;
    /** The content type of the options, undefined when the user cannot see it. */
    contentType: (ctg: string, ct: string) => EligibleContentType | undefined;
    /** The label of a select option, the value itself when it is not an option. */
    optionLabel: (ctg: string, ct: string, value: string) => string;
    /** A localized name (e.g. the object schema name), or `fallback`. */
    nameLabel: (names: LocalizedName, fallback: string) => string;
    /** The name of a company language, its ISO code when unknown. */
    languageLabel: (code: string) => string;
};

export const pickName = (
    names: LocalizedName,
    locale: string,
    defaultLocale: string | null,
): string | undefined =>
    names.find(name => name.languageIsoCode === locale)?.value ||
    names.find(name => name.languageIsoCode === defaultLocale)?.value ||
    names.find(name => !!name.value)?.value;

export const contentTypeKey = (ctg: string, ct: string) => `${ctg}/${ct}`;

export function buildContentTypeLabeler(
    options: FindReplaceOptions | null,
    locale: string,
): ContentTypeLabeler {
    const byKey = new Map<string, EligibleContentType>(
        (options?.contentTypes ?? []).map(contentType => [
            contentTypeKey(contentType.ctg, contentType.ct),
            contentType,
        ]),
    );
    const defaultLocale = options?.defaultLocale ?? null;
    const nameLabel = (names: LocalizedName, fallback: string) =>
        pickName(names ?? [], locale, defaultLocale) || fallback;

    return {
        groupLabel: ctg => {
            const contentType = options?.contentTypes.find(candidate => candidate.ctg === ctg);
            return (contentType && pickName(contentType.ctgName, locale, defaultLocale)) || ctg;
        },
        typeLabel: (ctg, ct) => {
            const contentType = byKey.get(contentTypeKey(ctg, ct));
            return (contentType && pickName(contentType.ctName, locale, defaultLocale)) || ct;
        },
        contentType: (ctg, ct) => byKey.get(contentTypeKey(ctg, ct)),
        optionLabel: (ctg, ct, value) => {
            const option = byKey
                .get(contentTypeKey(ctg, ct))
                ?.options?.find(candidate => candidate.value === value);
            return option ? nameLabel(option.label, value) : value;
        },
        nameLabel,
        languageLabel: code => options?.localeNames?.[code] || code,
    };
}
